/**
 * ผลของ action เปลี่ยนสถานะใบเสนอราคา — ตรวจ `status` ใน response ก่อนบอกผู้ใช้ว่า "สำเร็จ"
 *
 * E2E รอบ 2 SB-16 (API-REG-10 · 30 ก.ย. 2026): ยืนยันรับชนกับยกเลิก 10/10 รอบ `POST /accept` ตอบ 200
 * แต่ `status` ใน response = CANCELLED (backend อ่านใหม่หลัง commit — อีกคนยกเลิกทันทีหลังจากนั้น)
 * จอเดิมไม่ดู `status` → ขึ้น "ยืนยันสำเร็จ" กับใบที่ถูกยกเลิกไปแล้ว
 *
 * กติกา: ทุก action ที่คาดสถานะปลายทาง ต้องเทียบ `status` ที่ backend ตอบกับค่าที่คาด
 *   - ตรง → toast สำเร็จตามเดิม
 *   - ไม่ตรง → คำเตือน "ใบนี้ถูกเปลี่ยนเป็น '<ป้ายไทย>' โดยผู้ใช้อื่น" แทน toast สำเร็จ (ผู้เรียก refetch เสมอ)
 *   - response ไม่มี `status` → ตัดสินไม่ได้ ถือว่าสำเร็จ (backend ส่ง `status` ทุกครั้งตาม response_model
 *     — กรณีนี้เกิดเฉพาะ response ที่ไม่ใช่รูปใบเสนอราคา)
 */
import { QuotationStatus } from '../types/quotation';
import { showSuccess, showWarning } from './alert';

export type QuotationStatusAction = 'send' | 'accept' | 'reject' | 'cancel' | 'extend';

/**
 * สถานะปลายทางที่คาดของแต่ละ action (allow-list) — ต่ออายุ: SENT คง SENT · EXPIRED กลับเป็น ACCEPTED ถ้าลูกค้าเคยยอมรับ
 * (accepted_at) ไม่งั้น SENT (มติ PO 30 ก.ย. 2026 SB-09) → ต่ออายุจึงคาดได้ 2 ค่า
 */
export const EXPECTED_STATUS_AFTER_ACTION: Readonly<Record<QuotationStatusAction, ReadonlyArray<QuotationStatus>>> = {
  send: [QuotationStatus.SENT],
  accept: [QuotationStatus.ACCEPTED],
  reject: [QuotationStatus.REJECTED],
  cancel: [QuotationStatus.CANCELLED],
  extend: [QuotationStatus.SENT, QuotationStatus.ACCEPTED],
};

/** ป้ายไทยของสถานะ — ชุดเดียวกับตัวกรองสถานะของหน้ารายการ (เทสต์เทียบสองชุดให้ตรงกัน) */
export const QUOTATION_STATUS_LABELS: Readonly<Record<QuotationStatus, string>> = {
  [QuotationStatus.DRAFT]: 'แบบร่าง',
  [QuotationStatus.SENT]: 'ส่งแล้ว',
  [QuotationStatus.ACCEPTED]: 'ยอมรับแล้ว',
  [QuotationStatus.REJECTED]: 'ปฏิเสธแล้ว',
  [QuotationStatus.EXPIRED]: 'หมดอายุ',
  [QuotationStatus.CONVERTED]: 'สร้างรายการเช่าแล้ว',
  [QuotationStatus.CANCELLED]: 'ยกเลิก',
  [QuotationStatus.SUPERSEDED]: 'แทนที่แล้ว',
};

/** ป้ายไทยของสถานะ · สถานะที่ไม่รู้จัก (enum ใหม่ที่จอยังไม่มีป้าย) = คืนค่าเดิม ดีกว่าว่าง */
export const quotationStatusLabel = (status: string | null | undefined): string => {
  if (!status) return '';
  return Object.prototype.hasOwnProperty.call(QUOTATION_STATUS_LABELS, status)
    ? QUOTATION_STATUS_LABELS[status as QuotationStatus]
    : status;
};

export interface QuotationActionOutcome {
  /** true = สถานะใน response ตรงกับที่ action นี้คาด (หรือ response ไม่มี status ให้ตรวจ) */
  ok: boolean;
  /** สถานะจริงที่ backend ตอบ */
  actualStatus: string | null;
  /** คำเตือนพร้อมแสดง — มีเฉพาะเมื่อ ok=false */
  message: string | null;
}

export const quotationActionOutcome = (
  action: QuotationStatusAction,
  updated: { status?: QuotationStatus | string | null } | null | undefined
): QuotationActionOutcome => {
  const actual = updated?.status ?? null;
  if (!actual) return { ok: true, actualStatus: null, message: null };
  if (EXPECTED_STATUS_AFTER_ACTION[action].includes(actual as QuotationStatus)) {
    return { ok: true, actualStatus: actual, message: null };
  }
  return {
    ok: false,
    actualStatus: actual,
    message: `ใบนี้ถูกเปลี่ยนเป็น '${quotationStatusLabel(actual)}' โดยผู้ใช้อื่น`,
  };
};

/**
 * แจ้งผลของ action: สถานะตรง → toast สำเร็จ (`successMessage`) · ไม่ตรง → คำเตือน (ไม่มี toast สำเร็จ)
 * คืน outcome ให้ผู้เรียกใช้ต่อ (ผู้เรียกต้อง refetch เองทั้งสองกรณี)
 */
export const notifyQuotationActionResult = (
  action: QuotationStatusAction,
  updated: { status?: QuotationStatus | string | null } | null | undefined,
  successMessage: string
): QuotationActionOutcome => {
  const outcome = quotationActionOutcome(action, updated);
  if (outcome.ok) showSuccess(successMessage);
  else showWarning(outcome.message as string);
  return outcome;
};
